import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageSquare, X, Send, Maximize2, RefreshCw, Copy, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";

const STORAGE_KEY = "leo_widget_chat_id";
const READ_KEY = "leo_widget_last_read"; // per-chat: { [chat_id]: leoCount }

function countLeo(messages) {
  return (messages || []).filter(m => m.role === "leo").length;
}

function getReadMap() {
  try { return JSON.parse(localStorage.getItem(READ_KEY) || "{}"); } catch { return {}; }
}
function setReadFor(chatId, count) {
  const m = getReadMap();
  m[chatId] = count;
  localStorage.setItem(READ_KEY, JSON.stringify(m));
}

export default function LeoWidget() {
  const { user, lang } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const t = (k) => tr(k, lang);
  const [open, setOpen] = useState(false);
  const [chat, setChat] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [booting, setBooting] = useState(false);
  const [unread, setUnread] = useState(0);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [chatLimitReached, setChatLimitReached] = useState(false);
  const bottomRef = useRef(null);

  // Hide the widget entirely on splash/login/callback pages, full Leo page,
  // inside a scene, and on the quote-gate ritual screen.
  const hidden = !user
    || location.pathname === "/"
    || location.pathname.startsWith("/login")
    || location.pathname.startsWith("/auth")
    || location.pathname.startsWith("/leo")
    || location.pathname.startsWith("/scenes/")
    || location.pathname.startsWith("/enter");

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chat?.messages?.length, open]);

  const ensureChat = async () => {
    if (chat) return chat;
    setBooting(true);
    try {
      const savedId = localStorage.getItem(STORAGE_KEY);
      if (savedId) {
        try {
          const r = await api.get(`/leo/chats/${savedId}`);
          let data = r.data;
          if (!data.messages || data.messages.length === 0) {
            const o = await api.post(`/leo/chats/${savedId}/opening`);
            data = { ...data, messages: o.data.history };
          }
          setChat(data);
          return data;
        } catch { localStorage.removeItem(STORAGE_KEY); }
      }
      const list = await api.get("/leo/chats");
      let target = (list.data || [])[0];
      if (!target) {
        const created = await api.post("/leo/chats", { title: null, scene_text: "" });
        target = created.data;
      }
      localStorage.setItem(STORAGE_KEY, target.chat_id);
      const full = await api.get(`/leo/chats/${target.chat_id}`);
      let data = full.data;
      if (!data.messages || data.messages.length === 0) {
        const o = await api.post(`/leo/chats/${target.chat_id}/opening`);
        data = { ...data, messages: o.data.history };
      }
      setChat(data);
      return data;
    } finally { setBooting(false); }
  };

  const refreshUnread = useCallback(async () => {
    if (hidden || open) return;
    try {
      const savedId = localStorage.getItem(STORAGE_KEY);
      let target;
      if (savedId) {
        try { const r = await api.get(`/leo/chats/${savedId}`); target = r.data; }
        catch { localStorage.removeItem(STORAGE_KEY); }
      }
      if (!target) {
        const list = await api.get("/leo/chats");
        target = (list.data || [])[0];
        if (target) localStorage.setItem(STORAGE_KEY, target.chat_id);
      }
      if (!target) { setUnread(0); return; }
      const leoCount = countLeo(target.messages);
      const readMap = getReadMap();
      const readCount = readMap[target.chat_id] ?? leoCount;
      if (readMap[target.chat_id] === undefined) setReadFor(target.chat_id, leoCount);
      setUnread(Math.max(0, leoCount - readCount));
    } catch { /* ignore */ }
  }, [hidden, open]);

  useEffect(() => {
    if (hidden) { setUnread(0); return; }
    refreshUnread();
    const id = setInterval(refreshUnread, 30000);
    return () => clearInterval(id);
  }, [hidden, refreshUnread]);

  const handleOpen = async () => {
    setOpen(true);
    const c = chat || await ensureChat();
    if (c) {
      setReadFor(c.chat_id, countLeo(c.messages));
      setUnread(0);
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (chat) setReadFor(chat.chat_id, countLeo(chat.messages));
    setUnread(0);
  };

  // Cross-page bridge: LEO Radar (and other pages) dispatch a global
  // CustomEvent when the user clicks "Napisz wiadomość z LEO". We open the
  // widget, ensure a chat exists, then send a primer message to the same
  // Career Chat endpoint the widget already uses. No new UI, no new chat.
  useEffect(() => {
    const handler = async (e) => {
      if (hidden) return;
      const ctx = (e && e.detail) || {};
      setOpen(true);
      const c = chat || await ensureChat();
      if (!c) return;
      setReadFor(c.chat_id, countLeo(c.messages));
      setUnread(0);

      const roleLabel = ctx.person_role || "osoby z produkcji";
      const personLine = ctx.person_name
        ? `${ctx.person_name} (${roleLabel})`
        : `${roleLabel}`;
      const contactBlock = ctx.contact_markdown
        ? `\n\nWynik wyszukiwania o odbiorcy (ODBIORCA / KONTAKT / PRETEKSTY):\n${ctx.contact_markdown}\n`
        : "";
      const primer =
        `Chcę napisać wiadomość mailową do: ${personLine}.\n\n` +
        `=== KONTEKST TYLKO DLA CIEBIE (RADAR — NIE UJAWNIAJ TEGO W MAILU) ===\n` +
        `- Produkcja z LEO Radar: „${ctx.production_title || "(brak)"}”\n` +
        `- Reżyser: ${ctx.director || "(brak)"}\n` +
        `- Producent: ${ctx.producer || "(brak)"}\n` +
        `- Casting director: ${ctx.castingDirector || "(brak)"}\n` +
        contactBlock +
        `\n=== DWA TWARDE ZABEZPIECZENIA (zawsze, bez wyjątku) ===\n` +
        `1. RADAR_PRODUCTION ≠ PAST_COLLABORATION. Tytuł „${ctx.production_title || "…"}” to produkcja w przygotowaniu, monitorowana w LEO Radar — NIE jest wcześniejszą pracą użytkownika. NIGDY nie pisz „pracowaliśmy razem przy ${ctx.production_title || "…"}”, „na planie ${ctx.production_title || "…"}” ani niczego sugerującego, że użytkownik ma z tą produkcją cokolwiek wspólnego. Jeżeli użytkownik wybiera wcześniejszą współpracę, a nie zna szczegółów — dopytaj, nie zgaduj.\n` +
        `2. Produkcja z Radaru NIGDY nie jest pretekstem do kontaktu. NIE ujawniaj w mailu, że wiesz o „${ctx.production_title || "tej produkcji"}”. Zakazane zdania: „Wiem, że robicie ${ctx.production_title || "…"}”, „Słyszałem o ${ctx.production_title || "…"}”, „Gratuluję dofinansowania ${ctx.production_title || "…"}”, „Chciałbym zagrać w ${ctx.production_title || "…"}” oraz wszelkie ich odpowiedniki. Radar służy tylko do ustalenia, DO KOGO warto się odezwać — powód kontaktu musi być niezależny od tej produkcji.\n` +
        `\n` +
        `Poza tymi dwoma zabezpieczeniami działaj tak, jak zawsze w Career Chat — z Twoim normalnym stylem pisania maili aktorskich, dopytując użytkownika o brakujące fakty (czy się znają, przy czym pracowali, jakie materiały ma do wysłania, itd.).`;

      setChat(prev => ({
        ...(prev || c),
        messages: [
          ...(((prev || c).messages) || []),
          { role: "user", content: primer, ts: new Date().toISOString() },
        ],
      }));
      setSending(true);
      try {
        const r = await api.post(`/leo/chats/${c.chat_id}/message`, { message: primer });
        setChat(prev => {
          const next = { ...(prev || c), messages: r.data.history, title: r.data.title || prev?.title };
          setReadFor(next.chat_id, countLeo(next.messages));
          return next;
        });
        if (r.data.chat_limit_reached) setChatLimitReached(true);
      } catch {
        /* ignore */
      } finally {
        setSending(false);
      }
    };
    window.addEventListener("open-leo-with-context", handler);
    return () => window.removeEventListener("open-leo-with-context", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidden, chat]);

  const send = async () => {
    if (!input.trim() || sending || !chat || chatLimitReached) return;
    const text = input;
    setInput("");
    setSending(true);
    setChat(c => ({ ...c, messages: [...(c.messages || []), { role: "user", content: text, ts: new Date().toISOString() }] }));
    try {
      const r = await api.post(`/leo/chats/${chat.chat_id}/message`, { message: text });
      setChat(c => {
        const next = { ...c, messages: r.data.history, title: r.data.title || c.title };
        setReadFor(next.chat_id, countLeo(next.messages));
        return next;
      });
      if (r.data.chat_limit_reached) setChatLimitReached(true);
    } catch { /* ignore */ } finally { setSending(false); }
  };

  const openFullPage = () => {
    setOpen(false);
    if (chat) navigate(`/leo/${chat.chat_id}`);
    else navigate("/leo");
  };

  if (hidden) return null;

  return (
    <>
      {/* Floating button */}
      <AnimatePresence>
        {!open && (
          <motion.button
            data-testid="leo-widget-btn"
            data-onboarding="leo"
            key="leo-btn"
            initial={{ opacity: 0, scale: 0.85, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 12 }}
            transition={{ duration: 0.25 }}
            onClick={handleOpen}
            className="fixed bottom-6 right-6 z-[9999] w-14 h-14 rounded-full bg-amber-500 text-stone-950 shadow-lg shadow-amber-500/30 hover:bg-amber-400 active:scale-95 transition-colors flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950"
            aria-label={lang === "pl" ? "Otwórz czat z Leo" : "Open Leo chat"}
          >
            <MessageSquare size={22} strokeWidth={2.2}/>
            {unread > 0 ? (
              <span
                data-testid="leo-widget-unread-badge"
                className="absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] px-1 rounded-full bg-rose-500 text-white ring-2 ring-stone-950 text-[11px] font-semibold flex items-center justify-center mono"
              >
                {unread > 99 ? "99+" : unread}
              </span>
            ) : (
              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-stone-950"/>
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="leo-widget-panel"
            key="leo-panel"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="fixed z-[9999] bg-stone-950 border border-stone-800 rounded-sm shadow-2xl shadow-black/60 flex flex-col
                       bottom-4 right-4 left-4 h-[80vh]
                       sm:left-auto sm:right-6 sm:bottom-6 sm:w-[400px] sm:h-[560px]"
          >
            <header className="border-b border-stone-800 p-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center shrink-0">
                  <span className="serif text-amber-400 text-sm">L</span>
                </div>
                <div className="min-w-0">
                  <div className="serif text-stone-100 text-base leading-none">Leo</div>
                  <div className="mono text-[9px] tracking-[0.3em] text-amber-500/80 uppercase mt-1">
                    {lang === "pl" ? "Twój asystent · online" : "Your assistant · online"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid="leo-widget-expand-btn"
                  onClick={openFullPage}
                  className="text-stone-500 hover:text-amber-500 p-1.5 rounded-sm"
                  aria-label={lang === "pl" ? "Otwórz w pełnym oknie" : "Open full view"}
                >
                  <Maximize2 size={14}/>
                </button>
                <button
                  data-testid="leo-widget-close-btn"
                  onClick={handleClose}
                  className="text-stone-500 hover:text-stone-100 p-1.5 rounded-sm"
                  aria-label={lang === "pl" ? "Zamknij" : "Close"}
                >
                  <X size={16}/>
                </button>
              </div>
            </header>

            <div translate="no" data-testid="leo-transcript" className="notranslate flex-1 overflow-y-auto p-4 space-y-3">
              {booting && (
                <div className="text-amber-500/80 mono text-xs tracking-widest">
                  <RefreshCw size={12} className="inline animate-spin mr-2"/>{lang === "pl" ? "Leo dołącza…" : "Leo joining…"}
                </div>
              )}
              {chat && (chat.messages || []).map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid={`leo-widget-msg-${m.role}-${i}`}
                  className={m.role === "leo" ? "" : "text-right"}
                >
                  <div className={`mono text-[9px] tracking-widest uppercase ${m.role === "leo" ? "text-amber-500/70" : "text-stone-500 justify-end"}`}>
                    {m.role === "leo" ? "Leo" : (lang === "pl" ? "Ty" : "You")}
                  </div>
                  <div className={`mt-1 whitespace-pre-wrap leading-relaxed text-[14px] ${
                    m.role === "leo"
                      ? "text-stone-200 serif"
                      : "text-stone-300 mono inline-block bg-stone-900/60 px-2.5 py-1.5 rounded-sm max-w-[85%]"
                  }`}>{m.content}</div>
                  {m.role === "leo" && (m.content || "").trim() && (
                    <button
                      type="button"
                      data-testid={`leo-widget-copy-btn-${i}`}
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(m.content || "");
                          setCopiedIdx(i);
                          setTimeout(() => {
                            setCopiedIdx((cur) => (cur === i ? null : cur));
                          }, 2000);
                        } catch { /* ignore */ }
                      }}
                      className="mt-1 mono text-[9px] tracking-widest uppercase text-stone-500 hover:text-amber-400 inline-flex items-center gap-1"
                      aria-label={lang === "pl" ? "Kopiuj wiadomość" : "Copy message"}
                    >
                      {copiedIdx === i ? (
                        <><Check size={10}/> {lang === "pl" ? "Skopiowano ✓" : "Copied ✓"}</>
                      ) : (
                        <><Copy size={10}/> {lang === "pl" ? "Kopiuj wiadomość" : "Copy message"}</>
                      )}
                    </button>
                  )}
                </motion.div>
              ))}
              {sending && (
                <div className="text-amber-500/80 mono text-xs tracking-widest">
                  <RefreshCw size={12} className="inline animate-spin mr-2"/>{lang === "pl" ? "Leo myśli…" : "Leo is thinking…"}
                </div>
              )}
              <div ref={bottomRef}/>
            </div>

            {chatLimitReached && (
              <div
                data-testid="leo-widget-limit-msg"
                className="px-3 pt-2 mono text-[11px] text-amber-500/90"
              >
                {lang === "pl"
                  ? "Wykorzystałeś limit rozmów z LEO."
                  : "You've used your LEO chat allowance."}
                <button
                  type="button"
                  data-testid="leo-widget-limit-open-full"
                  onClick={openFullPage}
                  className="ml-2 underline hover:text-amber-400"
                >
                  {lang === "pl" ? "Otwórz pełny widok" : "Open full view"}
                </button>
              </div>
            )}

            <div className="border-t border-stone-800 p-3 flex gap-2">
              <input
                data-testid="leo-widget-input"
                value={input}
                onChange={(e)=>setInput(e.target.value)}
                onKeyDown={(e)=>{ if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }}}
                className="script-input flex-1 text-sm !py-2"
                placeholder={lang === "pl" ? "Napisz do Leo…" : "Write to Leo…"}
                disabled={!chat || chatLimitReached}
              />
              <button
                data-testid="leo-widget-send-btn"
                onClick={send}
                disabled={sending || !input.trim() || !chat || chatLimitReached}
                className="pill-btn !px-3 !py-2"
                aria-label={lang === "pl" ? "Wyślij" : "Send"}
              >
                <Send size={14}/>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
